import React, { useEffect, useState, useCallback } from 'react';
import { Trophy, Medal, Inbox } from 'lucide-react';
import api from '../../lib/api';
import { Spinner } from '../ui/Spinner';
import { Card } from '../ui/Card';
import { PeriodSelector } from './PeriodSelector';
import { fmtR, rColor } from '../../lib/tradeUtils';
import { cn } from '../../lib/utils';

function RankBadge({ rank }) {
  const medal = rank === 1 ? 'text-yellow-400' : rank === 2 ? 'text-zinc-300' : rank === 3 ? 'text-amber-600' : null;
  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center">
      {medal ? <Medal size={20} className={medal} /> : <span className="font-heading text-sm font-bold text-zinc-500 tnum">{rank}</span>}
    </div>
  );
}

function Row({ r, highlight }) {
  return (
    <div
      data-testid={`leaderboard-row-${r.rank}`}
      className={cn(
        'flex items-center gap-3 rounded-xl border px-4 py-3',
        highlight ? 'border-emerald/50 bg-emerald/5' : 'border-white/5 bg-ink-card'
      )}
    >
      <RankBadge rank={r.rank} />
      <div className="min-w-0 flex-1">
        <div className={cn('truncate font-heading font-semibold', highlight ? 'text-emerald' : 'text-white')}>{r.name}</div>
        <div className="text-xs text-zinc-500 tnum">{r.win_rate.toFixed(0)}% win · {r.trades} trades · {fmtR(r.avg_r, 2)} avg</div>
      </div>
      <div className={`font-heading text-lg font-bold tnum ${rColor(r.total_r)}`}>{fmtR(r.total_r, 1)}</div>
    </div>
  );
}

export default function Leaderboard() {
  const [period, setPeriod] = useState('this_month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/leaderboard?period=${period}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const you = data?.you;
  const youInTop = you && rows.some((r) => r.is_you);

  return (
    <div className="space-y-6" data-testid="leaderboard">
      <PeriodSelector value={period} onChange={setPeriod} label={data ? `${data.period_label} Leaderboard` : ''} />

      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <Trophy size={14} className="text-emerald" /> Ranked by cumulative R — performance, not account size.
      </div>

      {loading ? (
        <div className="grid h-64 place-items-center"><Spinner className="h-7 w-7" /></div>
      ) : data?.empty ? (
        <Card className="grid place-items-center gap-3 p-12 text-center" data-testid="leaderboard-empty">
          <Inbox className="text-zinc-600" size={28} />
          <div className="text-sm text-zinc-400">No ranked trades yet for this period.</div>
          <div className="text-xs text-zinc-600">Log trades in your journal to claim a spot on the leaderboard.</div>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            {rows.map((r) => <Row key={r.rank} r={r} highlight={r.is_you} />)}
          </div>
          {you && !youInTop && (
            <div className="space-y-2 border-t border-white/10 pt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Your position</div>
              <Row r={you} highlight />
            </div>
          )}
        </>
      )}
    </div>
  );
}
